import { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { isAndroid, isMobile } from 'react-device-detect';
import styled, { css, useTheme } from 'styled-components';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { FOOTER_HEIGHT_REM } from '../Footer';
import { FOOTER_HEIGHT_REM as FOOTERSG_HEIGHT_REM } from '../FooterSG';
import { useDelayedValue } from '../../hooks/use-delayed-value';

const POSITION_BOTTOM = isMobile
    ? FOOTER_HEIGHT_REM + FOOTERSG_HEIGHT_REM
    : FOOTER_HEIGHT_REM;

const VH_VAR_ON_KEYBOARD_OPEN = isAndroid ? '--vh-actual' : '--vh-frozen';

type EmojiPickerProps = {
    show: boolean;
    onSelect: (e: EmojiSelectEvent) => void;
    onClickOutside: () => void;
};

export type EmojiSelectEvent = {
    native: string;
    shortcodes: string;
};

const EXCEPT_EMOJI_IDS = [
    'middle_finger',
    'hankey',
    'face_with_symbols_on_mouth',
    'smiling_imp',
    'imp',
    'skull',
    'skull_and_crossbones',
    'tongue',
    'biting_lip',
    'couplekiss',
    'woman-kiss-man',
    'man-kiss-man',
    'woman-kiss-woman',
    'sweat_drops',
    'eggplant',
    'peach',
    'hocho',
    'briefs',
    'bikini',
    'nauseated_face',
    'face_vomiting',
    'clown_face',
    'japanese_ogre',
    'japanese_goblin',
    'alien',
    'space_invader',
    'person_in_motorized_wheelchair',
    'man_in_motorized_wheelchair',
    'woman_in_motorized_wheelchair',
    'person_in_manual_wheelchair',
    'man_in_manual_wheelchair',
    'woman_in_manual_wheelchair',
    'people_holding_hands',
    'two_women_holding_hands',
    'two_men_holding_hands',
    'men-with-bunny-ears-partying',
    'couple_with_heart',
    'man-heart-man',
    'woman-heart-woman',
    'family',
    'man-man-boy',
    'man-man-girl',
    'man-man-girl-boy',
    'man-man-boy-boy',
    'man-man-girl-girl',
    'woman-woman-boy',
    'woman-woman-girl',
    'woman-woman-girl-boy',
    'woman-woman-boy-boy',
    'woman-woman-girl-girl',
    'bath',
    'man_with_veil',
    'woman_in_tuxedo',
    'pregnant_man',
    'pregnant_person',
    'person_with_headscarf',
];

const CATEGORIES = [
    'frequent',
    'bingo',
    'classic',
    'people',
    'nature',
    'foods',
    'activity',
    'places',
    'objects',
    'symbols',
    'flags',
];

export const EmojiPickerContainer = styled.div<{ alignLeft: boolean }>`
    position: absolute;
    left: ${({ alignLeft }) => (alignLeft ? '0' : 'auto')};
    right: ${({ alignLeft }) => (alignLeft ? 'auto' : '0')};
    z-index: 15;
    bottom: ${POSITION_BOTTOM}rem;
    border: 0.125rem solid ${({ theme }) => theme.emoji.border};
    border-radius: 11px;
    transition: opacity linear 50ms;

    ${isMobile &&
    css`
        html[data-keyboard-open='true'] & {
            max-height: calc(
                var(${VH_VAR_ON_KEYBOARD_OPEN}, 100vh) -
                    var(--viewport-offset-top, 0px) - ${POSITION_BOTTOM}rem
            );
            overflow-y: auto;
        }
    `}
`;

export const EmojiPicker: FC<EmojiPickerProps> = ({
    show,
    onSelect,
    onClickOutside,
}) => {
    const theme = useTheme();
    const { customEmojiData } = theme;
    const containerRef = useRef<HTMLDivElement>(null);
    const [alignLeft, setAlignLeft] = useState(false);
    const [opacity, setOpacity] = useState(0);

    const $opacity = useDelayedValue(opacity, 1000 / 60);

    const updateAlignment = useCallback(() => {
        const containerEl = containerRef.current;
        if (!containerEl) {
            return;
        }

        const parentEl = containerEl.offsetParent as HTMLElement | null;
        if (!parentEl) {
            return;
        }

        const parentRect = parentEl.getBoundingClientRect();
        const containerWidth = containerEl.getBoundingClientRect().width;
        const leftIfRightAligned = parentRect.right - containerWidth;
        setAlignLeft(leftIfRightAligned < 0);
        setOpacity(1);
    }, []);

    useLayoutEffect(() => {
        if (!show) {
            return;
        }

        updateAlignment();
        window.addEventListener('resize', updateAlignment);

        const resizeObserver = new ResizeObserver(() => updateAlignment());
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', updateAlignment);
            resizeObserver.disconnect();
        };
    }, [show, updateAlignment]);

    useLayoutEffect(() => {
        setOpacity(0);
    }, [show]);

    if (!show) {
        return null;
    }

    return (
        <EmojiPickerContainer
            ref={containerRef}
            alignLeft={alignLeft}
            style={{ opacity: $opacity }}
        >
            <Picker
                data={data}
                previewPosition="none"
                custom={customEmojiData}
                onEmojiSelect={onSelect}
                onClickOutside={onClickOutside}
                maxFrequentRows={2}
                exceptEmojis={EXCEPT_EMOJI_IDS}
                categories={CATEGORIES}
            />
        </EmojiPickerContainer>
    );
};
